import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import LoginPage from '@app/pages/LoginPage';
import SignUpPage from '@app/pages/SignUpPage';
import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import RequireRole from '@app/components/router/RequireRole';
import { withLoading } from '@app/hocs/withLoading.hoc';
import DashboardPage from '@app/pages/DashboardPage';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));

const NewsListPage = React.lazy(() => import('@app/pages/news/NewsListPage'));
const NewsCreatePage = React.lazy(() => import('@app/pages/news/NewsCreatePage'));
const NewsEditPage = React.lazy(() => import('@app/pages/news/NewsEditPage'));
const BlogListPage = React.lazy(() => import('@app/pages/blog/BlogListPage'));
const BlogCreatePage = React.lazy(() => import('@app/pages/blog/BlogCreatePage'));
const BlogEditPage = React.lazy(() => import('@app/pages/blog/BlogEditPage'));
const PagesListPage = React.lazy(() => import('@app/pages/pages/PagesListPage'));
const PagesCreatePage = React.lazy(() => import('@app/pages/pages/PagesCreatePage'));
const PagesEditPage = React.lazy(() => import('@app/pages/pages/PagesEditPage'));
const SlidersListPage = React.lazy(() => import('@app/pages/sliders/SlidersListPage'));
const SlidersCreatePage = React.lazy(() => import('@app/pages/sliders/SlidersCreatePage'));
const SlidersEditPage = React.lazy(() => import('@app/pages/sliders/SlidersEditPage'));
const AnnouncementsListPage = React.lazy(() => import('@app/pages/announcements/AnnouncementsListPage'));
const AnnouncementsCreatePage = React.lazy(() => import('@app/pages/announcements/AnnouncementsCreatePage'));
const AnnouncementsEditPage = React.lazy(() => import('@app/pages/announcements/AnnouncementsEditPage'));
const MediaLibraryPage = React.lazy(() => import('@app/pages/media/MediaLibraryPage'));
const UsersListPage = React.lazy(() => import('@app/pages/users/UsersListPage'));
const UsersCreatePage = React.lazy(() => import('@app/pages/users/UsersCreatePage'));
const UsersEditPage = React.lazy(() => import('@app/pages/users/UsersEditPage'));
const ActivityLogsListPage = React.lazy(() => import('@app/pages/activityLogs/ActivityLogsListPage'));
const MenuListPage = React.lazy(() => import('@app/pages/menus/MenuListPage'));
const MenuCreatePage = React.lazy(() => import('@app/pages/menus/MenuCreatePage'));
const MenuEditPage = React.lazy(() => import('@app/pages/menus/MenuEditPage'));
const MuseumInfoPage = React.lazy(() => import('@app/pages/museumInfo/MuseumInfoPage'));
const HomeContentPage = React.lazy(() => import('@app/pages/homeContent/HomeContentPage'));
const ContactFormListPage = React.lazy(() => import('@app/pages/contactForms/ContactFormListPage'));
const ContactFormDetailPage = React.lazy(() => import('@app/pages/contactForms/ContactFormDetailPage'));
const ServerErrorPage = React.lazy(() => import('@app/pages/ServerErrorPage'));
const Error404Page = React.lazy(() => import('@app/pages/Error404Page'));

const Logout = React.lazy(() => import('./Logout'));

const Dashboard = withLoading(DashboardPage);
const NewsList = withLoading(NewsListPage);
const NewsCreate = withLoading(NewsCreatePage);
const NewsEdit = withLoading(NewsEditPage);
const BlogList = withLoading(BlogListPage);
const BlogCreate = withLoading(BlogCreatePage);
const BlogEdit = withLoading(BlogEditPage);
const PagesList = withLoading(PagesListPage);
const PagesCreate = withLoading(PagesCreatePage);
const PagesEdit = withLoading(PagesEditPage);
const SlidersList = withLoading(SlidersListPage);
const SlidersCreate = withLoading(SlidersCreatePage);
const SlidersEdit = withLoading(SlidersEditPage);
const AnnouncementsList = withLoading(AnnouncementsListPage);
const AnnouncementsCreate = withLoading(AnnouncementsCreatePage);
const AnnouncementsEdit = withLoading(AnnouncementsEditPage);
const MediaLibrary = withLoading(MediaLibraryPage);
const UsersList = withLoading(UsersListPage);
const UsersCreate = withLoading(UsersCreatePage);
const UsersEdit = withLoading(UsersEditPage);
const ActivityLogsList = withLoading(ActivityLogsListPage);
const MenuList = withLoading(MenuListPage);
const MenuCreate = withLoading(MenuCreatePage);
const MenuEdit = withLoading(MenuEditPage);
const MuseumInfo = withLoading(MuseumInfoPage);
const HomeContent = withLoading(HomeContentPage);
const ContactFormList = withLoading(ContactFormListPage);
const ContactFormDetail = withLoading(ContactFormDetailPage);

const ServerError = withLoading(ServerErrorPage);
const Error404 = withLoading(Error404Page);

const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={protectedLayout}>
          <Route index element={<Dashboard />} />
          
          {/* News Routes */}
          <Route path="news">
            <Route index element={<NewsList />} />
            <Route path="list" element={<NewsList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor', 'author']}>
                  <NewsCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor', 'author']}>
                  <NewsEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Blog Routes */}
          <Route path="blog">
            <Route index element={<BlogList />} />
            <Route path="list" element={<BlogList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor', 'author']}>
                  <BlogCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor', 'author']}>
                  <BlogEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Pages Routes */}
          <Route path="pages">
            <Route index element={<PagesList />} />
            <Route path="list" element={<PagesList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <PagesCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <PagesEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Sliders Routes */}
          <Route path="sliders">
            <Route index element={<SlidersList />} />
            <Route path="list" element={<SlidersList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <SlidersCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <SlidersEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Announcements Routes */}
          <Route path="announcements">
            <Route index element={<AnnouncementsList />} />
            <Route path="list" element={<AnnouncementsList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <AnnouncementsCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <AnnouncementsEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Media Routes */}
          <Route path="media">
            <Route index element={<MediaLibrary />} />
            <Route path="library" element={<MediaLibrary />} />
          </Route>

          {/* Users Routes */}
          <Route path="users">
            <Route
              index
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <UsersList />
                </RequireRole>
              }
            />
            <Route
              path="list"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <UsersList />
                </RequireRole>
              }
            />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin']}>
                  <UsersCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin']}>
                  <UsersEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Activity Logs Routes */}
          <Route path="activity-logs">
            <Route
              index
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <ActivityLogsList />
                </RequireRole>
              }
            />
            <Route
              path="list"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <ActivityLogsList />
                </RequireRole>
              }
            />
          </Route>

          {/* Menus Routes */}
          <Route path="menus">
            <Route index element={<MenuList />} />
            <Route path="list" element={<MenuList />} />
            <Route
              path="create"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <MenuCreate />
                </RequireRole>
              }
            />
            <Route
              path=":id/edit"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <MenuEdit />
                </RequireRole>
              }
            />
          </Route>

          {/* Museum Info Routes */}
          <Route
            path="museum-info"
            element={
              <RequireRole allowedRoles={['admin', 'editor']}>
                <MuseumInfo />
              </RequireRole>
            }
          />

          {/* Home Content Routes */}
          <Route
            path="home-content"
            element={
              <RequireRole allowedRoles={['admin', 'editor']}>
                <HomeContent />
              </RequireRole>
            }
          />

          {/* Contact Forms Routes */}
          <Route path="contact-forms">
            <Route
              index
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <ContactFormList />
                </RequireRole>
              }
            />
            <Route
              path="list"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <ContactFormList />
                </RequireRole>
              }
            />
            <Route
              path=":id"
              element={
                <RequireRole allowedRoles={['admin', 'editor']}>
                  <ContactFormDetail />
                </RequireRole>
              }
            />
          </Route>

          {/* Error Pages */}
          <Route path="server-error" element={<ServerError />} />
          <Route path="404" element={<Error404 />} />
        </Route>
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="sign-up" element={<SignUpPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<NewPasswordPage />} />
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
      </Routes>
    </BrowserRouter>
  );
};
